/**
 * One place that turns ANY image file into something the canvas can draw: the formats every browser
 * reads (JPEG, PNG, WebP, GIF, BMP, SVG), plus the ones that need help:
 *   - HEIC / HEIF (iPhone photos)  -> heic2any
 *   - AVIF                         -> the browser (createImageBitmap) where it can, otherwise a clear message
 *   - TIFF                         -> utif (browsers other than Safari cannot read TIFF)
 * Every image tool goes through this, so they accept the same files and give the same messages.
 */

export type ImageKind = "heic" | "avif" | "tiff" | "native";

const extOf = (name: string) => (name.toLowerCase().match(/\.([^./\\]+)$/) ?? [])[1] ?? "";

export function detectImageKind(file: { name: string; type: string }): ImageKind {
  const ext = extOf(file.name);
  const type = (file.type || "").toLowerCase();
  if (type === "image/heic" || type === "image/heif" || ext === "heic" || ext === "heif") return "heic";
  if (type === "image/avif" || ext === "avif") return "avif";
  if (type === "image/tiff" || ext === "tif" || ext === "tiff") return "tiff";
  return "native";
}

export class ImageDecodeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ImageDecodeError";
  }
}

/** A decoded image as a canvas at its full, upright size. */
export interface DecodedImage {
  canvas: HTMLCanvasElement;
  width: number;
  height: number;
  /** True when the source had transparent pixels worth keeping (PNG / WebP / GIF / SVG / AVIF). */
  mayHaveAlpha: boolean;
}

function canvasFrom(source: CanvasImageSource, w: number, h: number): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new ImageDecodeError("This browser could not create a drawing surface for the image.");
  ctx.drawImage(source, 0, 0, w, h);
  return canvas;
}

async function heicToBlob(file: Blob): Promise<Blob> {
  const heic2any = (await import("heic2any")).default;
  const out = await heic2any({ blob: file, toType: "image/png" });
  return Array.isArray(out) ? out[0] : out;
}

async function decodeTiff(file: Blob): Promise<HTMLCanvasElement> {
  const UTIF = (await import("utif")).default ?? (await import("utif"));
  const buf = await file.arrayBuffer();
  const ifds = UTIF.decode(buf);
  if (!ifds.length) throw new ImageDecodeError("This TIFF file has no readable picture in it.");
  UTIF.decodeImage(buf, ifds[0]);
  const rgba = UTIF.toRGBA8(ifds[0]);
  const w = ifds[0].width as number;
  const h = ifds[0].height as number;
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d")!;
  ctx.putImageData(new ImageData(new Uint8ClampedArray(rgba), w, h), 0, 0);
  return canvas;
}

function loadViaImg(blob: Blob): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
    img.src = url;
  });
}

/** Decode any supported image file to a canvas (EXIF orientation applied by the browser). */
export async function decodeImage(file: File): Promise<DecodedImage> {
  const kind = detectImageKind(file);
  try {
    if (kind === "tiff") {
      const canvas = await decodeTiff(file);
      return { canvas, width: canvas.width, height: canvas.height, mayHaveAlpha: true };
    }
    if (kind === "heic") {
      const png = await heicToBlob(file);
      const img = await loadViaImg(png);
      return { canvas: canvasFrom(img, img.naturalWidth, img.naturalHeight), width: img.naturalWidth, height: img.naturalHeight, mayHaveAlpha: false };
    }
    // native + AVIF: try the <img> route first (applies EXIF orientation everywhere), then createImageBitmap.
    try {
      const img = await loadViaImg(file);
      const w = img.naturalWidth || 300;
      const h = img.naturalHeight || 150;
      return { canvas: canvasFrom(img, w, h), width: w, height: h, mayHaveAlpha: true };
    } catch {
      if (typeof createImageBitmap === "function") {
        const bmp = await createImageBitmap(file);
        const c = canvasFrom(bmp, bmp.width, bmp.height);
        bmp.close?.();
        return { canvas: c, width: c.width, height: c.height, mayHaveAlpha: true };
      }
      throw new Error("decode");
    }
  } catch (e) {
    if (e instanceof ImageDecodeError) throw e;
    if (kind === "avif") throw new ImageDecodeError(`"${file.name}" is an AVIF image and this browser cannot open AVIF. Try Chrome, Edge, Firefox or a current Safari.`);
    if (kind === "heic") throw new ImageDecodeError(`"${file.name}" could not be read as an iPhone (HEIC) photo. The file may be damaged or use an unusual HEIC variant.`);
    throw new ImageDecodeError(`"${file.name}" could not be read as an image. The file may be damaged or not a picture.`);
  }
}

/** Fill a canvas's transparent pixels with a colour (white by default), for formats without alpha. */
export function flattenOnColor(src: HTMLCanvasElement, color = "#ffffff"): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = src.width;
  c.height = src.height;
  const ctx = c.getContext("2d")!;
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, c.width, c.height);
  ctx.drawImage(src, 0, 0);
  return c;
}

export function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (b) => {
        if (!b) return reject(new Error("This browser could not encode the image."));
        // A browser that cannot write the requested format silently hands back PNG. Refuse it
        // instead of returning PNG data that would be saved under a .webp/.gif/.bmp name.
        if (type !== "image/png" && b.type && b.type !== type) {
          return reject(new Error(`This browser cannot create ${type.replace("image/", "").toUpperCase()} images. Try Chrome, Edge or Firefox, or pick another format.`));
        }
        resolve(b);
      },
      type,
      quality,
    ),
  );
}

/** True when any pixel is not fully opaque (sampled on a down-scaled copy for speed). */
export function hasTransparency(canvas: HTMLCanvasElement): boolean {
  const s = Math.min(1, 256 / Math.max(canvas.width, canvas.height));
  const w = Math.max(1, Math.round(canvas.width * s));
  const h = Math.max(1, Math.round(canvas.height * s));
  const t = document.createElement("canvas");
  t.width = w; t.height = h;
  const ctx = t.getContext("2d")!;
  ctx.drawImage(canvas, 0, 0, w, h);
  const d = ctx.getImageData(0, 0, w, h).data;
  for (let i = 3; i < d.length; i += 4) if (d[i] < 250) return true;
  return false;
}

/** "photo.heic" -> "photo.jpg" etc.; keeps the original base name. */
export function renamedTo(name: string, ext: string, prefix = ""): string {
  return `${prefix}${name.replace(/\.[^./\\]+$/, "")}.${ext}`;
}
